import { Tooltip as BaseTooltip } from "@base-ui/react/tooltip";
import type { ReactElement, ReactNode } from "react";
import { cn } from "../lib/cn";
import { useShortcut } from "../lib/ShortcutProvider";
import { Kbd } from "./Kbd";

export interface TooltipProps {
  /** The element that shows the tooltip. Must accept a ref and event props (a button, usually). */
  children: ReactElement;
  content: ReactNode;
  /** A combo like `"mod+k"`, rendered as keys after the content. */
  shortcut?: string | undefined;
  shortcutId?: string;
  side?: BaseTooltip.Positioner.Props["side"];
  align?: BaseTooltip.Positioner.Props["align"];
  sideOffset?: number;
  open?: boolean;
  onOpenChange?: BaseTooltip.Root.Props["onOpenChange"];
  disabled?: boolean;
  className?: string;
}

/**
 * Wrap `TooltipProvider` around the app once so tooltips share hover delay and
 * open instantly when moving between adjacent triggers.
 */
export const TooltipProvider = BaseTooltip.Provider;

export function Tooltip({
  children,
  content,
  shortcut,
  shortcutId,
  side = "top",
  align = "center",
  sideOffset = 6,
  open,
  onOpenChange,
  disabled,
  className,
}: TooltipProps) {
  const configuredShortcut = useShortcut(shortcutId ?? "");
  const keys = shortcutId ? configuredShortcut : shortcut;
  return (
    <BaseTooltip.Root
      {...(open !== undefined ? { open } : {})}
      {...(onOpenChange !== undefined ? { onOpenChange } : {})}
      {...(disabled !== undefined ? { disabled } : {})}
    >
      <BaseTooltip.Trigger render={children} />
      <BaseTooltip.Portal>
        <BaseTooltip.Positioner
          side={side}
          align={align}
          sideOffset={sideOffset}
          className="z-tooltip"
        >
          <BaseTooltip.Popup
            data-slot="tooltip"
            className={cn(
              "flex items-center gap-2 rounded-md border border-border bg-surface-raised px-2 py-1 text-xs text-fg shadow-md origin-(--transform-origin) transition-[opacity,scale] duration-fast ease-enter data-starting-style:scale-[0.97] data-starting-style:opacity-0 data-ending-style:scale-[0.97] data-ending-style:opacity-0 data-instant:transition-none",
              className,
            )}
          >
            <span>{content}</span>
            {keys !== undefined && <Kbd keys={keys} className="-mr-0.5" />}
          </BaseTooltip.Popup>
        </BaseTooltip.Positioner>
      </BaseTooltip.Portal>
    </BaseTooltip.Root>
  );
}
